import { KeyboardEvent, useCallback, useRef, useState } from 'react';
import { eventKeyEnter } from '@legacy/constants/keyboardKey';
import { useInitialFocus } from '@legacy/hooks/useInitialFocus';
import { getKeyboardFocusableElements } from '@legacy/utils/accessibility';
import {
  blurSelectElement,
  keyboardAction,
  reloadKeyboardSelectedElement,
} from '@legacy/utils/keyboardUpDownEvent';

interface IUseKeyboardControl {
  backAction?: () => void;
  hasLazyLoad?: boolean;
  selector?: string;
}

export const useKeyboardControl = ({
  backAction,
  hasLazyLoad,
  selector = '[role="button"]',
}: IUseKeyboardControl) => {
  // Initial focus
  const initialFocusElement = useRef<HTMLInputElement | null>(null);
  useInitialFocus(initialFocusElement);

  const searchContainerResults = useRef<HTMLUListElement>(null);
  const [selectItemIndex, setSelectItemIndex] = useState<number>(-1);

  // Create the debounced function outside of useCallback
  const debouncedKeyDownHandler = (event: KeyboardEvent<HTMLDivElement>) => {
    const focusableElements = getKeyboardFocusableElements(
      searchContainerResults?.current,
      selector,
    );
    const currentSelectedItem = focusableElements[selectItemIndex];
    if (focusableElements.length === 1 && event.key === 'Enter') {
      focusableElements[0].click();
      return;
    }
    if (focusableElements.length > 0) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      const isNeedToBlur = keyboardAction({
        event,
        setSelectItemIndex,
        selectItemIndex,
        focusableElements,
        hasLazyLoad,
      });
      if (isNeedToBlur) {
        blurSelectElement(currentSelectedItem);
      }
    }
  };

  const textFieldOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      debouncedKeyDownHandler(event);
    },
    [debouncedKeyDownHandler],
  );

  const dialogOnKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' && event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      backAction?.();
    }

    if (event.key === eventKeyEnter && (event.ctrlKey || event.metaKey)) {
      getKeyboardFocusableElements(
        searchContainerResults?.current,
        '[class*="ariaSelected"] button[type="button"]',
      )?.[1].click();
    }
  };

  reloadKeyboardSelectedElement(
    getKeyboardFocusableElements(
      searchContainerResults?.current,
      "[role='button']",
    )?.[selectItemIndex],
    initialFocusElement,
  );

  return {
    dialogOnKeyDown,
    initialFocusElement,
    searchContainerResults,
    selectItemIndex,
    setSelectItemIndex,
    textFieldOnKeyDown,
  };
};
